import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

function Chevron({ open }) {
  return (
    <svg
      className={`cp-picker-chevron${open ? " is-open" : ""}`}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function optionMeta(school, listSort) {
  if (listSort === "distance" && Number.isFinite(Number(school.miles))) {
    return `${Number(school.miles).toFixed(1)} mi`;
  }
  if (listSort === "rank" && Number.isFinite(Number(school.gcseRank))) {
    return `GCSE #${school.gcseRank}`;
  }
  return [school.area, school.gender].filter(Boolean).join(" · ");
}

/** Searchable school picker for one compare column. */
export default function SchoolPicker({
  schools,
  selectedId,
  excludeIds = [],
  onSelect,
  onClear,
  label = "Select school",
  listSort = "distance",
  onListSortChange,
  hasHome = false,
  sortOptions = [],
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuBox, setMenuBox] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);
  const controlRef = useRef(null);

  const selected = useMemo(
    () => schools.find((school) => Number(school.id) === Number(selectedId)) || null,
    [schools, selectedId]
  );

  const blocked = useMemo(
    () => new Set((excludeIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))),
    [excludeIds]
  );

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return schools.filter((school) => {
      if (blocked.has(Number(school.id)) && Number(school.id) !== Number(selectedId)) return false;
      if (!needle) return true;
      const hay = `${school.name || ""} ${school.area || ""} ${school.gender || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [schools, blocked, selectedId, query]);

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return undefined;
    }
    function place() {
      const node = controlRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      const width = Math.max(rect.width, 260);
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      const top = rect.bottom + 6;
      const maxHeight = Math.max(160, Math.min(320, window.innerHeight - top - 12));
      setMenuBox({ top, left, width, maxHeight });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onPointer(event) {
      const inRoot = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inRoot && !inMenu) setOpen(false);
    }
    function onKey(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    const id = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.cancelAnimationFrame(id);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  function openMenu() {
    setOpen(true);
  }

  const menu =
    open && menuBox
      ? createPortal(
          <div
            ref={menuRef}
            className="cp-picker-menu cp-picker-menu--portal"
            role="listbox"
            aria-label="Schools"
            style={{
              top: menuBox.top,
              left: menuBox.left,
              width: menuBox.width,
              maxHeight: menuBox.maxHeight,
            }}
          >
            {sortOptions.length ? (
              <div className="cp-picker-sort" role="group" aria-label="List order">
                <span className="cp-picker-sort-label">List by</span>
                {sortOptions.map((option) => {
                  const disabled = option.id === "distance" && !hasHome;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className={`cp-picker-sort-btn${listSort === option.id ? " is-on" : ""}`}
                      aria-pressed={listSort === option.id}
                      disabled={disabled}
                      title={
                        disabled
                          ? "Set a home postcode to sort by distance"
                          : `Sort all school lists by ${option.label.toLowerCase()}`
                      }
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!disabled) onListSortChange?.(option.id);
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="cp-picker-list">
              {visible.length === 0 ? (
                <p className="cp-picker-empty">No school matches.</p>
              ) : (
                visible.map((school) => {
                  const on = Number(school.id) === Number(selectedId);
                  const meta = optionMeta(school, listSort);
                  return (
                    <button
                      key={school.id}
                      type="button"
                      role="option"
                      aria-selected={on}
                      className={`cp-picker-option${on ? " is-on" : ""}`}
                      onClick={() => {
                        onSelect?.(school.id);
                        setOpen(false);
                        setQuery("");
                      }}
                    >
                      <span className="cp-picker-option-name">{school.name}</span>
                      {meta ? <span className="cp-picker-option-meta">{meta}</span> : null}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`cp-picker${open ? " is-open" : ""}${selected ? " has-school" : ""}`} ref={rootRef}>
      <label className="cp-picker-field">
        <span className="cp-picker-kicker">{selected ? "School" : "Select school"}</span>
        <span className="cp-picker-control" ref={controlRef}>
          {selected && !open ? (
            <button
              type="button"
              className="cp-picker-selected"
              aria-haspopup="listbox"
              aria-expanded={open}
              onClick={openMenu}
            >
              <span className="cp-picker-selected-text">
                <span className="cp-picker-value">{selected.name}</span>
                {selected.area ? <span className="cp-picker-meta">{selected.area}</span> : null}
              </span>
              <Chevron open={open} />
            </button>
          ) : (
            <>
              <input
                ref={searchRef}
                className="cp-picker-search-inline"
                type="search"
                value={query}
                placeholder={label}
                aria-label={label}
                aria-haspopup="listbox"
                aria-expanded={open}
                onFocus={openMenu}
                onClick={openMenu}
                onChange={(event) => {
                  setQuery(event.target.value);
                  if (!open) setOpen(true);
                }}
              />
              <button
                type="button"
                className="cp-picker-toggle"
                aria-label={open ? "Hide school list" : "Show school list"}
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
              >
                <Chevron open={open} />
              </button>
            </>
          )}
        </span>
      </label>

      {selected ? (
        <button
          type="button"
          className="cp-picker-clear"
          aria-label="Clear school"
          onClick={() => {
            onClear?.();
            setOpen(false);
          }}
        >
          ✕
        </button>
      ) : null}

      {menu}
    </div>
  );
}
